import React from 'react'
import { Helmet } from 'react-helmet'
import { useIntl } from 'react-intl'
import { useLocation, matchPath } from 'react-router-dom'
import { translationMessages, supportedLocales, defaultLocale } from 'i18n'
import { messages as routeMessages } from 'routes'
import useUrl from 'useUrl'
import { useBrand } from 'contexts/BrandContext'
import { brands, DEFAULT_BRAND } from 'brands'

// Convert internal locale keys (e.g. "en-gb") to BCP 47 hreflang values (e.g. "en-GB")
const toBCP47 = (locale) => {
  const [lang, ...region] = locale.split('-')
  return region.length ? `${lang}-${region.join('-').toUpperCase()}` : lang
}

// Find the best route match with params extracted.
// useMatch(location.pathname) in React Router v6 treats the pathname as a
// literal pattern so params are never extracted. Instead we try each known
// route pattern (skipping the catch-all) and return the first real match.
export const findRouteMatch = (pathname, locale) => {
  for (const [name, msg] of Object.entries(routeMessages)) {
    if (name === 'explore') continue // catch-all — skip, use as last resort
    const pattern = translationMessages[locale][msg.id]
    if (!pattern) continue
    // Convert intl {param} placeholders to React Router :param syntax
    const routerPattern = pattern.replace(/\{(\w+)\}/g, ':$1')
    const m = matchPath(routerPattern, pathname)
    if (m) return m
  }
  // Fallback: return a no-param match so canonicals at least points to root
  return matchPath('/*', pathname) || { params: {}, pattern: { path: '' }, pathname }
}

// One alternate per locale the brand serves, on the brand's own host.
export const canonicals = (match, locale, brand = brands[DEFAULT_BRAND]) => {
  // React Router v6 puts the pattern at match.pattern.path; v5 used match.path
  const matchedPath = match.pattern?.path || match.path || ''

  let path = Object.keys(match.params).reduce((path, key) => {
    if (key === '*') {
      return path.replace('*', '')
    }
    return path.replace(`:${key}`, `{${key}}`)
  }, matchedPath)

  const messageKey = Object.keys(translationMessages[locale]).find(
    (key) => translationMessages[locale][key] === path
  )

  const locales = brand.locales.filter((l) => l in supportedLocales)

  return locales.reduce((memo, alternateLocale) => {
    const basePath = translationMessages[alternateLocale][messageKey]

    if (!basePath) {
      memo[alternateLocale] = useUrl(alternateLocale, null, true, false, brand)
      return memo
    }

    const path = Object.entries(match.params).reduce((memo, [name, val]) => {
      const translatedVal = translationMessages[alternateLocale][
        Object.keys(translationMessages[locale]).find(
          (key) => translationMessages[locale][key] === val
        )
      ]
      return memo.replace(`{${name}}`, translatedVal || val)
    }, basePath)

    memo[alternateLocale] = useUrl(alternateLocale, path, true, false, brand)
    return memo
  }, {})
}

export const Canonicals = ({}) => {
  const location = useLocation()
  const { locale } = useIntl()
  const brand = useBrand()

  const match = findRouteMatch(location.pathname, locale)
  const alternates = canonicals(match, locale, brand)
  const xDefault = alternates[defaultLocale] || alternates[brand.locales[0]]

  // Strip trailing slash for a consistent no-trailing-slash canonical policy.
  // useUrl treats null/empty as "base URL" (no trailing slash).
  const canonicalPath = location.pathname.replace(/\/$/, '') || null

  return (
    <Helmet>
      <link rel="canonical" href={useUrl(locale, canonicalPath, true, false, brand)} />
      <link rel="alternate" href={xDefault} hreflang="x-default" />
      {Object.entries(alternates).map(([alternateLocale, href]) => (
        <link
          key={alternateLocale}
          rel="alternate"
          href={href}
          hreflang={toBCP47(alternateLocale)}
        />
      ))}
    </Helmet>
  )
}

export default Canonicals
